import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, TrendingUp, Package, Truck, FileCheck, Clock, AlertTriangle, DollarSign, BarChart3 } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, BarChart, Bar, Legend, Cell, PieChart, Pie } from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-lg border transition-all hover:scale-[1.02]" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{label}</p>
          <p className="text-2xl font-bold mt-1" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5" style={{ color: color || 'var(--accent-primary)', opacity: 0.7 }} />}
      </div>
    </div>
  );
}

function SCurveChart({ data, title }) {
  const { isDark } = useTheme();
  if (!data?.length) return <div className="h-64 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>No data available</div>;

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{title}</h4>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <defs>
            <linearGradient id="planGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
          <XAxis dataKey="period_label" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
          <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }}
            formatter={(v) => [`${v}%`]} />
          <ReferenceLine x={new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit' })} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: 'Today', fill: '#F59E0B', fontSize: 10 }} />
          <Area type="monotone" dataKey="plan" stroke="#3B82F6" fill="url(#planGrad)" strokeWidth={2} name="Planned" />
          <Area type="monotone" dataKey="actual" stroke="#10B981" fill="url(#actualGrad)" strokeWidth={2} name="Actual" />
          <Line type="monotone" dataKey="forecast" stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 4" name="Forecast" dot={false} />
          <Legend />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProcurementFunnel({ summary }) {
  const { isDark } = useTheme();
  const stages = [
    { name: 'Total Items', value: summary?.totalLineItems || 0, color: '#6366F1' },
    { name: 'MR Issued', value: summary?.mrIssued || 0, color: 'var(--tone-navy-text)' },
    { name: 'PO Placed', value: summary?.poPlaced || 0, color: '#3B82F6' },
    { name: 'At Site', value: summary?.atSite || 0, color: '#10B981' },
    { name: 'GRN Done', value: summary?.grnDone || 0, color: '#059669' }
  ];

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Procurement Funnel</h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={stages} layout="vertical" margin={{ left: 80, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: isDark ? '#cbd5e1' : '#374151' }} width={80} />
          <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
            {stages.map((s, i) => <Cell key={i} fill={s.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CategoryBreakdown({ summary }) {
  const { isDark } = useTheme();
  const data = [
    { name: 'Bulk', value: summary?.byCategoryBreakdown?.bulk?.total || 0, color: '#5EEAD4', progress: summary?.byCategoryBreakdown?.bulk?.avgProgress || 0 },
    { name: 'Tagged', value: summary?.byCategoryBreakdown?.tagged?.total || 0, color: '#C4A8FF', progress: summary?.byCategoryBreakdown?.tagged?.avgProgress || 0 }
  ];

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Category Breakdown</h4>
      <div className="flex items-center gap-6">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-3 flex-1">
          {data.map(d => (
            <div key={d.name}>
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} /> <span style={{ color: 'var(--text-primary)' }}>{d.name}</span></span>
                <span style={{ color: 'var(--text-muted)' }}>{d.value} items | {d.progress.toFixed(1)}%</span>
              </div>
              <div className="h-1.5 rounded-full mt-1" style={{ background: 'var(--card-bg-alt)' }}>
                <div className="h-full rounded-full" style={{ width: `${d.progress}%`, background: d.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MaterialTypeChart({ summary }) {
  const { isDark } = useTheme();
  const data = (summary?.byMaterialType || []).map(t => ({
    name: t.materialTypeCode,
    total: t.totalItems,
    completed: t.completedItems,
    overdue: t.overdueItems,
    progress: t.averageProgress
  }));

  if (!data.length) return null;

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Progress by Material Type</h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} angle={-30} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
          <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
          <Legend />
          <Bar dataKey="completed" stackId="a" fill="#10B981" name="Completed" radius={[0, 0, 0, 0]} />
          <Bar dataKey="overdue" stackId="a" fill="#EF4444" name="Overdue" radius={[0, 0, 0, 0]} />
          <Bar dataKey="total" fill="#3B82F6" name="Total" opacity={0.3} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MCIAnalytics({ projectId }) {
  const [summary, setSummary] = useState(null);
  const [sCurveData, setSCurveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [curveType, setCurveType] = useState('overall');

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [sumRes, scRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mci/summary`),
        api.get(`/v1/projects/${projectId}/mci/summary/s-curve?interval=week`)
      ]);
      setSummary(sumRes.data);
      setSCurveData(scRes.data.s_curve_data || []);
    } catch (e) { toast.error('Failed to load analytics'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="mci-analytics">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPICard label="Total Items" value={summary?.totalLineItems || 0} icon={Package} color="var(--accent-primary)" />
        <KPICard label="MRs Issued" value={summary?.mrIssued || 0} sub={`${((summary?.mrIssued || 0) / Math.max(summary?.totalLineItems, 1) * 100).toFixed(1)}%`} icon={FileCheck} color="#143A6B" />
        <KPICard label="POs Placed" value={summary?.poPlaced || 0} sub={`${((summary?.poPlaced || 0) / Math.max(summary?.totalLineItems, 1) * 100).toFixed(1)}%`} icon={DollarSign} color="#3B82F6" />
        <KPICard label="At Site" value={summary?.atSite || 0} icon={Truck} color="#10B981" />
        <KPICard label="GRN Done" value={summary?.grnDone || 0} icon={FileCheck} color="#059669" />
        <KPICard label="Overall Progress" value={`${(summary?.overallProgress || 0).toFixed(1)}%`} icon={TrendingUp} color="var(--accent-primary)" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Long Lead Items" value={summary?.longLeadItems?.totalFlagged || 0} sub={`${summary?.longLeadItems?.delayed || 0} delayed`} icon={Clock} color="#F59E0B" />
        <KPICard label="PO Value" value={`$${((summary?.totalPOValue || 0) / 1000000).toFixed(1)}M`} icon={DollarSign} color="#6366F1" />
        <KPICard label="Overdue" value={summary?.overdue || 0} sub={`${((summary?.overdue || 0) / Math.max(summary?.totalLineItems, 1) * 100).toFixed(1)}%`} icon={AlertTriangle} color="#EF4444" />
        <KPICard label="Completed" value={summary?.completed || 0} icon={FileCheck} color="#059669" />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SCurveChart data={sCurveData} title="MCI S-Curve - Overall Progress" />
        <ProcurementFunnel summary={summary} />
        <CategoryBreakdown summary={summary} />
        <MaterialTypeChart summary={summary} />
      </div>
    </div>
  );
}
